import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './DispenseChart.css';

const DispenseChart = ({ dispenseHistory = [] }) => {
  const [timeRange, setTimeRange] = useState('7d');
  const [dispenseType, setDispenseType] = useState('All');

  const processedData = useMemo(() => {
    if (!Array.isArray(dispenseHistory)) {
      console.warn('Dispense history is not an array:', dispenseHistory);
      return [];
    }

      const now = new Date();
    let startDate = new Date();
      
    switch (timeRange) {
        case '24h':
        startDate.setDate(now.getDate() - 1);
          break;
        case '7d':
        startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
        startDate.setDate(now.getDate() - 30);
          break;
      default:
        startDate = new Date(0); // Epoch for lifetime
    }

    const filtered = dispenseHistory.filter(d => {
      if (!d || !d.timestamp) {
        console.warn('Invalid dispense record:', d);
        return false;
      }
      const dispenseDate = new Date(d.timestamp);
      const typeMatch = dispenseType === 'All' || d.mode === dispenseType;
      return dispenseDate >= startDate && typeMatch;
    });
      
    // Aggregate data by day for a cleaner chart
    const aggregated = filtered.reduce((acc, curr) => {
      const date = new Date(curr.timestamp).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = { date, dispenses: 0 };
      }
      acc[date].dispenses += 1;
      return acc;
    }, {});

    return Object.values(aggregated).sort((a,b) => new Date(a.date) - new Date(b.date));
  }, [dispenseHistory, timeRange, dispenseType]);

  // Calculate metrics
  const metrics = useMemo(() => {
    if (!Array.isArray(dispenseHistory)) return { total: 0, thisWeek: 0, today: 0, avgDaily: 0 };

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Start of current week (Sunday)
    
    const total = dispenseHistory.length;
    const today = dispenseHistory.filter(d => new Date(d.timestamp) >= startOfToday).length;
    const thisWeek = dispenseHistory.filter(d => new Date(d.timestamp) >= startOfWeek).length;
    
    // Calculate average daily dispenses based on SELECTED FILTERS (timeRange and dispenseType)
    let avgDaily = 0;
    if (timeRange !== 'lifetime') {
      // Determine the time range for average calculation
      let startDate = new Date();
      let days = 1;
      
      switch (timeRange) {
        case '24h':
          startDate.setDate(now.getDate() - 1);
          days = 1;
          break;
        case '7d':
          startDate.setDate(now.getDate() - 7);
          days = 7;
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          days = 30;
          break;
      }
      
      // Filter dispenses by selected time range AND dispense type
      const filteredDispenses = dispenseHistory.filter(d => {
        if (!d || !d.timestamp) return false;
        const dispenseDate = new Date(d.timestamp);
        const timeMatch = dispenseDate >= startDate;
        const typeMatch = dispenseType === 'All' || d.mode === dispenseType;
        return timeMatch && typeMatch;
      });
      
      avgDaily = Math.round((filteredDispenses.length / days) * 10) / 10;
    } else {
      // For lifetime, calculate average over all days since first dispense
      const typeFilteredDispenses = dispenseHistory.filter(d => {
        const typeMatch = dispenseType === 'All' || d.mode === dispenseType;
        return typeMatch;
      });
      
      if (typeFilteredDispenses.length > 0) {
        const oldestDispense = new Date(Math.min(...typeFilteredDispenses.map(d => new Date(d.timestamp))));
        const daysSinceFirst = Math.ceil((now - oldestDispense) / (1000 * 60 * 60 * 24)) || 1;
        avgDaily = Math.round((typeFilteredDispenses.length / daysSinceFirst) * 10) / 10;
      }
    }

    return { total, thisWeek, today, avgDaily };
  }, [dispenseHistory, timeRange, dispenseType]);

  return (
    <div className="dispense-chart-container">
      <div className="metrics-container">
        <div className="metric-box">
          <h4>Total Dispenses</h4>
          <p>{metrics.total}</p>
        </div>
        <div className="metric-box">
          <h4>This Week</h4>
          <p>{metrics.thisWeek}</p>
        </div>
        <div className="metric-box">
          <h4>Today</h4>
          <p>{metrics.today}</p>
        </div>
        <div className="metric-box">
          <h4>Avg Daily</h4>
          <p>{metrics.avgDaily}</p>
        </div>
      </div>
      
      <div className="chart-controls">
        <select value={dispenseType} onChange={(e) => setDispenseType(e.target.value)}>
          <option value="All">All Dispenses</option>
          <option value="FREE">Free Dispenses</option>
          <option value="PAYMENT">Paid Dispenses</option>
        </select>
        <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="lifetime">Lifetime</option>
        </select>
      </div>

      {processedData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={processedData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="dispenses" stroke="#8884d8" activeDot={{ r: 8 }} />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="no-data-message">
          <p>No dispense data available for the selected period.</p>
            </div>
      )}
    </div>
  );
};

export default DispenseChart; 